@file:Suppress("DEPRECATION")
@file:NoLiveLiterals
package org.example.newportfolio.components.widgets.chat

import androidx.compose.runtime.*
import androidx.compose.runtime.NoLiveLiterals
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.style.*
import com.varabyte.kobweb.silk.components.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlin.js.json
import org.example.newportfolio.theme.brand
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input
import org.w3c.fetch.RequestInit

private val SYSTEM_PROMPT = """You are Mohamed Elgohary's AI assistant on his portfolio website. You are a world-class Senior Software Engineer, Product Manager, and Business Analyst with over 40 years of combined experience.

Your expertise includes:
- Mobile Development (Android/Kotlin, Flutter/Dart, Jetpack Compose, KMP)
- Web Development (Kotlin/JS, Kobweb, React)
- Product Strategy, Roadmapping, and User Research
- Business Analysis, Requirements Engineering, and Stakeholder Management
- System Architecture, Clean Architecture, MVVM, MVI patterns
- Firebase, REST APIs, GraphQL, Databases (Room, Realm, PostgreSQL, MongoDB)

You represent Mohamed Elgohary — a software engineer & product builder who takes software products end‑to‑end (analysis, UI/UX, backend, web, mobile, and QA) and also designs and ships his own Shopify apps and SaaS. When answering:
- Be professional, confident, and concise
- Provide actionable technical advice when asked
- Help potential clients understand how Mohamed can solve their problems
- Recommend the best technology stack for their project needs
- If asked about pricing or timelines, suggest they contact Mohamed directly
- Keep responses focused and under 150 words unless detail is needed"""

// ──── Styles ────────────────────────────────────────────────────────────────

val ChatInputStyle by ComponentStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(10.px, 12.px)
            .backgroundColor(Colors.Transparent)
            .border(0.px)
            .color(Colors.White)
            .fontSize(14.px)
            .outline(0.px)
            .styleModifier {
                property("font-family", "inherit")
            }
    }
}

val ChatButtonStyle by ComponentStyle {
    base {
        val palette = colorMode.toPalette()
        Modifier
            .position(Position.Fixed)
            .bottom(20.px)
            .right(20.px)
            .size(56.px)
            .borderRadius(50.percent)
            .cursor(Cursor.Pointer)
            .zIndex(1000)
            .display(DisplayStyle.Flex)
            .transition(CSSTransition("all", 0.3.s, TransitionTimingFunction.EaseOut))
            .styleModifier {
                property("background", "linear-gradient(135deg, ${palette.brand.primary} 0%, rgba(168, 85, 247, 1) 100%)")
                property("box-shadow", "0 8px 32px rgba(147, 51, 234, 0.4), 0 0 0 0 rgba(147, 51, 234, 0.3)")
                property("align-items", "center")
                property("justify-content", "center")
            }
    }
    Breakpoint.MD {
        Modifier
            .bottom(30.px)
            .right(30.px)
            .size(62.px)
    }
    hover {
        Modifier.transform { scale(1.1) }
            .styleModifier {
                property("box-shadow", "0 12px 40px rgba(147, 51, 234, 0.5), 0 0 20px rgba(147, 51, 234, 0.2)")
            }
    }
}

val ChatWindowStyle by ComponentStyle {
    base {
        Modifier
            .position(Position.Fixed)
            .bottom(90.px)
            .right(12.px)
            .left(12.px)
            .height(70.vh)
            .maxHeight(520.px)
            .borderRadius(20.px)
            .zIndex(1001)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .overflow(Overflow.Hidden)
            .transition(CSSTransition("all", 0.3.s, TransitionTimingFunction.EaseOut))
            .styleModifier {
                property("background", "linear-gradient(180deg, rgba(30, 27, 46, 0.98) 0%, rgba(18, 16, 28, 0.99) 100%)")
                property("border", "1px solid rgba(147, 51, 234, 0.2)")
                property("box-shadow", "0 25px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(147, 51, 234, 0.1)")
                property("backdrop-filter", "blur(20px)")
            }
    }
    Breakpoint.MD {
        Modifier
            .bottom(105.px)
            .right(30.px)
            .width(380.px)
            .height(520.px)
            .styleModifier { property("left", "auto") }
    }
}

val ChatMessageListStyle by ComponentStyle {
    base {
        Modifier.fillMaxWidth().flexGrow(1)
            .overflow { y(Overflow.Auto) }
            .styleModifier {
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
            }
    }
    cssRule("&::-webkit-scrollbar") {
        Modifier.display(DisplayStyle.None)
    }
}

// ──── Component ─────────────────────────────────────────────────────────────

@Composable
fun GeminiChatBot() {
    var isOpened by remember { mutableStateOf(false) }
    var inputMessage by remember { mutableStateOf("") }
    val messages = remember { mutableStateListOf<ChatMessage>() }
    val scope = rememberCoroutineScope()
    var isLoading by remember { mutableStateOf(false) }

    // No size on this wrapper — children are position: fixed and don't need a sized parent.
    // Previously fillMaxSize() caused ~500px of empty space before the footer.
    Box {
        // ── Floating Action Button ──
        Box(
            modifier = ChatButtonStyle.toModifier().onClick { isOpened = !isOpened },
            contentAlignment = Alignment.Center
        ) {
            if (isOpened) {
                FaXmark(modifier = Modifier.color(Colors.White).fontSize(20.px))
            } else {
                FaRobot(modifier = Modifier.color(Colors.White).fontSize(22.px))
            }
        }

        // ── Chat Window ──
        if (isOpened) {
            Box(modifier = ChatWindowStyle.toModifier()) {
                // Header
                Row(
                    modifier = Modifier.fillMaxWidth().padding(16.px, 18.px)
                        .styleModifier {
                            property("background", "linear-gradient(135deg, rgba(147, 51, 234, 0.15) 0%, rgba(168, 85, 247, 0.08) 100%)")
                            property("border-bottom", "1px solid rgba(147, 51, 234, 0.15)")
                        },
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        // AI avatar
                        Box(
                            modifier = Modifier.size(36.px).borderRadius(50.percent)
                                .margin(right = 10.px)
                                .display(DisplayStyle.Flex)
                                .styleModifier {
                                    property("background", "linear-gradient(135deg, rgba(147, 51, 234, 0.6) 0%, rgba(168, 85, 247, 0.4) 100%)")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            FaRobot(modifier = Modifier.color(Colors.White).fontSize(16.px))
                        }
                        Column {
                            SpanText(
                                "AI Assistant",
                                modifier = Modifier.color(Colors.White).fontSize(14.px)
                                    .fontWeight(FontWeight.SemiBold)
                            )
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Box(
                                    modifier = Modifier.size(6.px).borderRadius(50.percent)
                                        .backgroundColor(Color.rgb(74, 222, 128))
                                        .margin(right = 5.px)
                                )
                                SpanText(
                                    "Online",
                                    modifier = Modifier.color(Color.rgb(74, 222, 128)).fontSize(11.px)
                                )
                            }
                        }
                    }
                    Box(
                        modifier = Modifier
                            .size(32.px).borderRadius(50.percent)
                            .display(DisplayStyle.Flex)
                            .cursor(Cursor.Pointer)
                            .backgroundColor(Color.rgba(255, 255, 255, 0.05f))
                            .onClick { isOpened = false },
                        contentAlignment = Alignment.Center
                    ) {
                        FaXmark(modifier = Modifier.color(Color.rgba(255, 255, 255, 0.6f)).fontSize(14.px))
                    }
                }

                // Messages
                Column(
                    modifier = ChatMessageListStyle.toModifier()
                        .padding(top = 12.px, bottom = 8.px, left = 16.px, right = 16.px)
                ) {
                    if (messages.isEmpty()) {
                        Box(
                            modifier = Modifier.fillMaxWidth().padding(top = 30.px),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                Box(
                                    modifier = Modifier.size(56.px).borderRadius(50.percent)
                                        .margin(bottom = 16.px)
                                        .display(DisplayStyle.Flex)
                                        .styleModifier {
                                            property("background", "linear-gradient(135deg, rgba(147, 51, 234, 0.2) 0%, rgba(168, 85, 247, 0.1) 100%)")
                                            property("align-items", "center")
                                            property("justify-content", "center")
                                        },
                                    contentAlignment = Alignment.Center
                                ) {
                                    FaRobot(modifier = Modifier.color(Color.rgba(168, 85, 247, 0.7f)).fontSize(24.px))
                                }
                                SpanText(
                                    "Hi! I'm Mohamed's AI assistant.",
                                    modifier = Modifier.color(Colors.White).fontSize(15.px)
                                        .fontWeight(FontWeight.Medium).margin(bottom = 6.px)
                                )
                                SpanText(
                                    "Ask me about projects, tech stack,\nor how we can help your business.",
                                    modifier = Modifier.color(Color.rgba(255, 255, 255, 0.45f))
                                        .fontSize(13.px)
                                        .styleModifier { property("text-align", "center") }
                                )
                            }
                        }
                    }

                    messages.forEach { msg ->
                        Box(
                            modifier = Modifier.fillMaxWidth().margin(bottom = 10.px),
                            contentAlignment = if (msg.isUser) Alignment.CenterEnd else Alignment.CenterStart
                        ) {
                            if (!msg.isUser) {
                                Row(verticalAlignment = Alignment.Top) {
                                    Box(
                                        modifier = Modifier.size(26.px).borderRadius(50.percent)
                                            .margin(right = 8.px, top = 2.px)
                                            .display(DisplayStyle.Flex)
                                            .flexShrink(0)
                                            .styleModifier {
                                                property("background", "linear-gradient(135deg, rgba(147, 51, 234, 0.5) 0%, rgba(168, 85, 247, 0.3) 100%)")
                                                property("align-items", "center")
                                                property("justify-content", "center")
                                            },
                                        contentAlignment = Alignment.Center
                                    ) {
                                        FaRobot(modifier = Modifier.color(Colors.White).fontSize(12.px))
                                    }
                                    Box(
                                        modifier = Modifier
                                            .padding(10.px, 14.px)
                                            .borderRadius(topLeft = 4.px, topRight = 16.px, bottomLeft = 16.px, bottomRight = 16.px)
                                            .maxWidth(78.percent)
                                            .styleModifier {
                                                property("background", "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)")
                                                property("border", "1px solid rgba(255,255,255,0.06)")
                                            }
                                    ) {
                                        SpanText(
                                            msg.text,
                                            modifier = Modifier.color(Color.rgba(255, 255, 255, 0.9f))
                                                .fontSize(13.px)
                                                .lineHeight(1.5.em)
                                        )
                                    }
                                }
                            } else {
                                Box(
                                    modifier = Modifier
                                        .padding(10.px, 14.px)
                                        .borderRadius(topLeft = 16.px, topRight = 4.px, bottomLeft = 16.px, bottomRight = 16.px)
                                        .maxWidth(78.percent)
                                        .styleModifier {
                                            property("background", "linear-gradient(135deg, rgba(147, 51, 234, 0.7) 0%, rgba(126, 34, 206, 0.6) 100%)")
                                        }
                                ) {
                                    SpanText(
                                        msg.text,
                                        modifier = Modifier.color(Colors.White).fontSize(13.px)
                                            .lineHeight(1.5.em)
                                    )
                                }
                            }
                        }
                    }

                    if (isLoading) {
                        Row(
                            modifier = Modifier.padding(left = 34.px, top = 4.px, bottom = 4.px),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            SpanText(
                                "Thinking",
                                modifier = Modifier.color(Color.rgba(168, 85, 247, 0.7f)).fontSize(12.px)
                                    .margin(right = 4.px)
                            )
                            SpanText(
                                "...",
                                modifier = Modifier.color(Color.rgba(168, 85, 247, 0.5f)).fontSize(12.px)
                            )
                        }
                    }
                }

                // Input area
                Row(
                    modifier = Modifier.fillMaxWidth().padding(12.px, 16.px)
                        .styleModifier {
                            property("border-top", "1px solid rgba(147, 51, 234, 0.1)")
                            property("background", "rgba(255,255,255,0.02)")
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Input(
                        type = InputType.Text,
                        attrs = ChatInputStyle.toModifier().onKeyDown { evt ->
                            if (evt.key == "Enter" && inputMessage.isNotBlank() && !isLoading) {
                                val msg = inputMessage
                                inputMessage = ""
                                scope.launch { sendMessage(msg, messages) { isLoading = it } }
                            }
                        }.toAttrs {
                            placeholder("Ask me anything...")
                            onInput { inputMessage = it.value }
                            value(inputMessage)
                        }
                    )
                    Box(
                        modifier = Modifier
                            .size(36.px).borderRadius(50.percent)
                            .display(DisplayStyle.Flex)
                            .cursor(if (inputMessage.isNotBlank() && !isLoading) Cursor.Pointer else Cursor.Default)
                            .styleModifier {
                                val bg = if (inputMessage.isNotBlank() && !isLoading)
                                    "linear-gradient(135deg, rgba(147, 51, 234, 0.8) 0%, rgba(168, 85, 247, 0.6) 100%)"
                                else
                                    "rgba(255,255,255,0.05)"
                                property("background", bg)
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("transition", "all 0.2s ease")
                            }
                            .onClick {
                                if (inputMessage.isNotBlank() && !isLoading) {
                                    val msg = inputMessage
                                    inputMessage = ""
                                    scope.launch { sendMessage(msg, messages) { isLoading = it } }
                                }
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        FaPaperPlane(
                            modifier = Modifier
                                .color(if (inputMessage.isNotBlank() && !isLoading) Colors.White else Color.rgba(255, 255, 255, 0.25f))
                                .fontSize(13.px)
                        )
                    }
                }
            }
        }
    }
}

// ──── Data & API ────────────────────────────────────────────────────────────

data class ChatMessage(val text: String, val isUser: Boolean)

private fun extractGeminiText(result: dynamic): String? {
    return try {
        result.candidates[0].content.parts[0].text as String
    } catch (e: Throwable) {
        null
    }
}

private suspend fun tryGemini(question: String): String? {
    val response = window.fetch(
        "/api/chat",
        RequestInit(
            method = "POST",
            headers = json("Content-Type" to "application/json"),
            body = JSON.stringify(
                json(
                    "contents" to arrayOf(
                        json(
                            "parts" to arrayOf(
                                json("text" to "$SYSTEM_PROMPT\n\nUser question: $question")
                            )
                        )
                    )
                )
            )
        )
    ).await()

    if (!response.ok) return null
    val result = response.json().await()
    return extractGeminiText(result.asDynamic())
}

private fun smartFallback(question: String): String {
    val q = question.lowercase()
    return when {
        q.contains("hello") || q.contains("hi") || q.contains("hey") ->
            "Hello! I'm Mohamed's AI assistant. I can help you learn about his skills in mobile development, discuss your project needs, or answer technical questions. What would you like to know?"

        q.contains("price") || q.contains("cost") || q.contains("rate") || q.contains("charge") || q.contains("budget") ->
            "For pricing and project estimates, it's best to contact Mohamed directly. Each project is unique, and he provides custom quotes based on scope, timeline, and complexity. Reach out via email at mohamed.abdelazim.elgohary@gmail.com or Telegram @MohamedElgohary88."

        q.contains("android") || q.contains("kotlin") || q.contains("mobile") || q.contains("app") ->
            "Mohamed specializes in native Android development with Kotlin, Jetpack Compose, and Clean Architecture. He builds production-ready apps with MVVM/MVI patterns, Room/Realm databases, and integrates Firebase, REST APIs, and GraphQL. He can take your app from concept to Play Store."

        q.contains("flutter") || q.contains("cross") || q.contains("ios") || q.contains("dart") ->
            "Mohamed has strong cross-platform experience with Flutter and Dart, as well as Kotlin Multiplatform (KMP). He can build apps that run on both Android and iOS from a single codebase while maintaining native performance and feel."

        q.contains("web") || q.contains("website") || q.contains("kobweb") ->
            "This portfolio itself is built with Kobweb (Kotlin/JS). Mohamed has experience building web applications with modern frameworks. For full-stack needs, he works with Ktor for backend services and various databases including PostgreSQL and MongoDB."

        q.contains("experience") || q.contains("background") || q.contains("work") ->
            "Mohamed is a Software Engineer specializing in mobile development. He has worked on e-commerce platforms, fintech apps, and enterprise solutions. His expertise spans Android (Kotlin/Compose), Flutter, and web development with a focus on clean, scalable architecture."

        q.contains("contact") || q.contains("email") || q.contains("reach") || q.contains("hire") ->
            "You can reach Mohamed at:\n• Email: mohamed.abdelazim.elgohary@gmail.com\n• Telegram: @MohamedElgohary88\n• LinkedIn: linkedin.com/in/mohamed-elgohary8\n• GitHub: github.com/MohamedElgohary88"

        q.contains("skill") || q.contains("tech") || q.contains("stack") || q.contains("tool") ->
            "Mohamed's tech stack includes:\n• Languages: Kotlin, Dart, Java, Swift\n• Mobile: Jetpack Compose, Flutter, KMP\n• Architecture: MVVM, MVI, Clean Architecture\n• Backend: Ktor, Firebase, REST, GraphQL\n• Databases: Room, Realm, PostgreSQL, MongoDB\n• Tools: Android Studio, Git, Figma, Jira"

        q.contains("project") || q.contains("portfolio") || q.contains("build") ->
            "Mohamed has built a range of projects including e-commerce apps (Ramtex), productivity tools (PostSync), and more. Check out the Works section on this site for detailed case studies. He can handle projects from ideation to deployment."

        q.contains("time") || q.contains("timeline") || q.contains("how long") || q.contains("deadline") ->
            "Project timelines depend on scope and complexity. A simple MVP might take 4-6 weeks, while a full-featured app could be 2-4 months. Contact Mohamed directly to discuss your specific requirements and get an accurate estimate."

        else ->
            "Thanks for your question! Mohamed is a software engineer & product builder who takes products end‑to‑end — web, mobile, backend, and QA — with Kotlin, Flutter, KMP, Laravel and Remix/TypeScript, and he ships his own Shopify apps and SaaS. For specific inquiries, feel free to reach out at mohamed.abdelazim.elgohary@gmail.com or explore the Works section to see his projects."
    }
}

suspend fun sendMessage(question: String, messages: MutableList<ChatMessage>, setLoading: (Boolean) -> Unit) {
    messages.add(ChatMessage(question, true))
    setLoading(true)

    try {
        // Try Gemini via server proxy, fall back to smart static responses
        val text = tryGemini(question) ?: smartFallback(question)
        messages.add(ChatMessage(text, false))
    } catch (e: Exception) {
        messages.add(ChatMessage(smartFallback(question), false))
    } finally {
        setLoading(false)
    }
}
